import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { FaUsers, FaBriefcase, FaGraduationCap, FaTrophy, FaArrowRight, FaLinkedin, FaGoogle, FaSearch, FaStar, FaNewspaper, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { getAlmamater, parseEducation } from '../utils/education';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import usePageTitle from '../hooks/usePageTitle';

/* ── Animated counter hook ── */
const useCounter = (end, duration = 1800) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isVisible = useRef(false);
  const prevEnd = useRef(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { isVisible.current = entry.isIntersecting; },
      { threshold: 0.3 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Re-trigger animation whenever `end` changes (e.g. data finishes loading)
  useEffect(() => {
    if (end === 0 || end === prevEnd.current) return;
    prevEnd.current = end;

    // If already visible or was visible before, animate immediately
    const startTime = performance.now();
    const step = (now) => {
      const progress = Math.min((now - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * end));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [end, duration]);

  return [count, ref];
};

const StatCard = ({ icon, value, label, delay }) => {
  const [count, ref] = useCounter(typeof value === 'number' ? value : 0);
  return (
    <div
      ref={ref}
      className="home-stat-card group relative bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-5 md:p-7 hover:bg-white/20 transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl"
      style={{ animationDelay: `${delay}ms` }}
    >
      <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-white/5 to-transparent pointer-events-none" />
      <div className="relative">
        <div className="w-12 h-12 md:w-14 md:h-14 rounded-xl bg-gradient-to-br from-gold-400 to-gold-600 flex items-center justify-center text-white mb-4 shadow-lg group-hover:scale-110 transition-transform duration-300">
          {icon}
        </div>
        <div className="text-3xl md:text-4xl font-extrabold text-white mb-1">
          {typeof value === 'number' ? count : value}
        </div>
        <div className="text-sm text-white/70 font-medium">{label}</div>
      </div>
    </div>
  );
};

const Home = () => {
  usePageTitle();
  const { isAdmin } = useAuth();
  const { t } = useLanguage();
  const [alumni, setAlumni] = useState([]);
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fameIndex, setFameIndex] = useState(0);

  useEffect(() => {
    Promise.all([
      supabase.from('alumni').select('*').order('id', { ascending: false }),
      supabase.from('news').select('id, title, slug, excerpt, cover_url, category, created_at')
        .eq('is_published', true).order('created_at', { ascending: false }).limit(3),
    ]).then(([alumniRes, newsRes]) => {
      setAlumni(alumniRes.data || []);
      setNews(newsRes.data || []);
    }).finally(() => setLoading(false));
  }, []);

  // Stats
  const totalAlumni = alumni.length;
  const uniqueJobs = [...new Set(alumni.map(a => a.job))].length;
  const uniqueEdu = [...new Set(alumni.map(a => getAlmamater(a.education)).filter(Boolean))].length;
  const uniquePimnas = [...new Set(alumni.map(a => a.tahun_pimnas).filter(Boolean))].length;

  // Hall of Fame — rotates every 5 seconds, only alumni with jobs (shuffled)
  const famePool = React.useMemo(() => {
    const pool = alumni.filter(a => a.job && a.photo_url);
    for (let i = pool.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [pool[i], pool[j]] = [pool[j], pool[i]];
    }
    return pool;
  }, [alumni]);
  useEffect(() => {
    if (famePool.length <= 3) return;
    const timer = setInterval(() => {
      setFameIndex(prev => (prev + 3) % famePool.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [famePool.length]);

  const featured = famePool.length <= 3
    ? famePool
    : [0, 1, 2].map(i => famePool[(fameIndex + i) % famePool.length]);

  const nextFame = () => setFameIndex(prev => (prev + 3) % famePool.length);
  const prevFame = () => setFameIndex(prev => (prev - 3 + famePool.length) % famePool.length);

  const formatDate = (dateStr) =>
    new Date(dateStr).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' });

  const statCards = [
    { icon: <FaUsers className="text-xl md:text-2xl" />, value: totalAlumni, label: t('home.totalAlumni') },
    { icon: <FaBriefcase className="text-xl md:text-2xl" />, value: uniqueJobs, label: t('home.careerFields') },
    { icon: <FaGraduationCap className="text-xl md:text-2xl" />, value: uniqueEdu, label: t('home.eduInstitutions') },
    { icon: <FaTrophy className="text-xl md:text-2xl" />, value: uniquePimnas || 'PIMNAS', label: uniquePimnas ? t('home.pimnasEditions') : t('home.nationalCompetition') },
  ];

  const categoryColor = (cat) => {
    if (cat === 'Prestasi') return 'bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200';
    if (cat === 'Event') return 'bg-blue-100 text-blue-700 ring-1 ring-blue-200';
    if (cat === 'Kolaborasi') return 'bg-violet-100 text-violet-700 ring-1 ring-violet-200';
    if (cat === 'Program') return 'bg-amber-100 text-amber-700 ring-1 ring-amber-200';
    return 'bg-gray-100 text-gray-700 ring-1 ring-gray-200';
  };

  return (
    <div className="font-inter overflow-x-hidden">
      {/* ─── HERO ─── */}
      <section className="relative min-h-[92vh] md:min-h-[88vh] flex flex-col justify-center overflow-hidden bg-gradient-to-br from-primary-900 via-primary-800 to-indigo-900">
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="home-float-1 absolute -top-20 -right-20 w-[500px] h-[500px] bg-gold-500 opacity-[0.07] rounded-full blur-3xl"></div>
          <div className="home-float-2 absolute -bottom-32 -left-32 w-[600px] h-[600px] bg-primary-400 opacity-[0.08] rounded-full blur-3xl"></div>
          <div className="home-float-3 absolute top-1/3 left-1/4 w-72 h-72 bg-indigo-400 opacity-[0.05] rounded-full blur-3xl"></div>
          {/* Geometric grid pattern */}
          <div className="absolute inset-0 opacity-[0.03]" style={{backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '40px 40px'}}></div>
          {/* Diagonal lines */}
          <div className="absolute top-0 right-0 w-full h-full opacity-[0.04]" style={{backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 60px, white 60px, white 61px)'}}></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 w-full">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left: text content */}
            <div className="text-center lg:text-left home-hero-content">
              <div className="inline-flex items-center gap-2 bg-gold-500/15 backdrop-blur-sm text-gold-400 text-sm font-semibold px-5 py-2 rounded-full mb-8 border border-gold-500/20">
                <FaTrophy className="text-gold-400" />
                <span>{t('home.badge')}</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-extrabold text-white leading-[1.1] mb-6 tracking-tight">
                {t('home.heroTitle1')}{' '}
                <span className="relative inline-block">
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-gold-400 via-amber-300 to-gold-500">PIMNAS</span>
                  <span className="absolute -bottom-2 left-0 right-0 h-1 bg-gradient-to-r from-gold-400 to-amber-300 rounded-full opacity-60"></span>
                </span>
                <br className="hidden sm:block" />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-white to-gray-300">{t('home.heroTitle2')}</span>
              </h1>
              <p className="text-base sm:text-lg lg:text-xl text-gray-300/90 mb-10 leading-relaxed max-w-xl mx-auto lg:mx-0">
                {t('home.heroDesc')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Link
                  to="/alumni"
                  className="group inline-flex items-center justify-center gap-3 bg-gradient-to-r from-gold-500 to-amber-500 hover:from-gold-600 hover:to-amber-600 text-white font-bold py-4 px-9 rounded-2xl shadow-lg shadow-gold-500/25 hover:shadow-xl hover:shadow-gold-500/30 transition-all duration-300 transform hover:-translate-y-1"
                >
                  <FaSearch className="group-hover:rotate-12 transition-transform duration-300" />
                  {t('home.exploreAlumni')}
                </Link>
                <Link
                  to={isAdmin ? '/add-alumni' : '/ajukan-alumni'}
                  className="group inline-flex items-center justify-center gap-3 bg-white/10 backdrop-blur-sm border-2 border-white/20 hover:border-white/40 hover:bg-white/15 text-white font-bold py-4 px-9 rounded-2xl transition-all duration-300 transform hover:-translate-y-1"
                >
                  {isAdmin ? t('nav.addAlumni') : t('home.registerYourself')}
                  <FaArrowRight className="text-sm group-hover:translate-x-1 transition-transform duration-300" />
                </Link>
              </div>
            </div>

            {/* Right: Stats grid (desktop) */}
            <div className="hidden lg:grid grid-cols-2 gap-4">
              {statCards.map((s, i) => (
                <StatCard key={i} icon={s.icon} value={s.value} label={s.label} delay={i * 150} />
              ))}
            </div>
          </div>
        </div>

        {/* Smooth wave transition */}
        <svg className="absolute bottom-0 left-0 w-full" viewBox="0 0 1440 120" fill="none" preserveAspectRatio="none" style={{height: '80px'}}>
          <path d="M0 60C240 120 480 0 720 60C960 120 1200 20 1440 80V120H0V60Z" fill="#f9fafb"/>
        </svg>
      </section>

      {/* ─── MOBILE STATS (visible < lg) ─── */}
      <section className="lg:hidden bg-gray-50 pb-8 -mt-1">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-2 gap-3 sm:gap-4 -mt-8 relative z-10">
            {statCards.map((s, i) => (
              <div
                key={i}
                className="bg-white rounded-2xl p-4 sm:p-5 shadow-lg border border-gray-100 home-stat-card"
                style={{ animationDelay: `${i * 100}ms` }}
              >
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center text-white mb-3 text-base">
                  {s.icon}
                </div>
                <div className="text-2xl sm:text-3xl font-extrabold text-gray-900">{s.value}</div>
                <div className="text-xs sm:text-sm text-gray-500 mt-0.5 font-medium">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── FEATURED ALUMNI ─── */}
      <section className="bg-gray-50 py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section header */}
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between mb-12">
            <div className="home-fade-up">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-gold-400 to-gold-600 flex items-center justify-center">
                  <FaStar className="text-white text-sm" />
                </div>
                <span className="text-sm font-bold text-gold-600 uppercase tracking-wider">{t('home.spotlight')}</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 tracking-tight">{t('home.hallOfFame')}</h2>
              <p className="text-gray-500 mt-2 text-lg">{t('home.hallOfFameDesc')}</p>
            </div>
            <div className="mt-6 sm:mt-0 flex items-center gap-3">
              {famePool.length > 3 && (
                <div className="flex gap-2 mr-3">
                  <button onClick={prevFame} className="w-10 h-10 rounded-xl bg-white border border-gray-200 hover:border-primary-300 hover:bg-primary-50 flex items-center justify-center text-gray-500 hover:text-primary-600 transition-all shadow-sm">
                    <FaChevronLeft className="text-sm" />
                  </button>
                  <button onClick={nextFame} className="w-10 h-10 rounded-xl bg-white border border-gray-200 hover:border-primary-300 hover:bg-primary-50 flex items-center justify-center text-gray-500 hover:text-primary-600 transition-all shadow-sm">
                    <FaChevronRight className="text-sm" />
                  </button>
                </div>
              )}
              <Link to="/alumni" className="text-primary-600 hover:text-primary-700 font-semibold inline-flex items-center gap-2 px-5 py-2.5 rounded-xl hover:bg-primary-50 transition-all">
                {t('home.viewAll')} <FaArrowRight className="text-sm" />
              </Link>
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center py-20">
              <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin"></div>
            </div>
          ) : featured.length === 0 ? (
            <div className="text-center py-20 text-gray-400">
              <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-5">
                <FaUsers className="text-3xl text-gray-300" />
              </div>
              <p className="text-lg">{t('home.noData')} <Link to="/add-alumni" className="text-primary-600 hover:underline font-semibold">{t('home.addNow')}</Link></p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
              {featured.map((al, idx) => (
                <Link
                  key={al.id}
                  to={`/alumni/${al.id}`}
                  className="home-fame-card group relative bg-white rounded-2xl shadow-sm hover:shadow-2xl border border-gray-100 hover:border-primary-200 transition-all duration-500 hover:-translate-y-2 overflow-hidden"
                  style={{ animationDelay: `${idx * 120}ms` }}
                >
                  {/* Gradient top bar */}
                  <div className="h-24 bg-gradient-to-r from-primary-600 via-primary-500 to-gold-500 relative">
                    <div className="absolute inset-0 opacity-20" style={{backgroundImage: 'radial-gradient(circle at 30% 50%, white 1px, transparent 1px)', backgroundSize: '20px 20px'}}></div>
                    {al.tahun_pimnas && (
                      <div className="absolute top-4 right-4 bg-white/20 backdrop-blur-sm text-white text-xs font-bold px-3 py-1 rounded-full border border-white/30">
                        PIMNAS ke-{al.tahun_pimnas}
                      </div>
                    )}
                  </div>
                  {/* Avatar */}
                  <div className="flex justify-center -mt-10 relative z-10">
                    {al.photo_url ? (
                      <img src={al.photo_url} alt={al.name} className="w-20 h-20 rounded-2xl object-cover ring-4 ring-white shadow-lg group-hover:scale-105 transition-transform duration-300" />
                    ) : (
                      <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center text-white text-2xl font-bold ring-4 ring-white shadow-lg group-hover:scale-105 transition-transform duration-300">
                        {al.name?.charAt(0)}
                      </div>
                    )}
                  </div>
                  {/* Content */}
                  <div className="p-6 pt-4 text-center">
                    <h3 className="text-lg font-bold text-gray-900 group-hover:text-primary-600 transition-colors truncate">{al.name}</h3>
                    <p className="text-sm text-gray-500 mt-1 truncate">{getAlmamater(al.education)}</p>
                    {(() => {
                      const edu = parseEducation(al.education);
                      const highest = edu.s3_kampus ? `S3 — ${edu.s3_kampus}` : edu.s2_kampus ? `S2 — ${edu.s2_kampus}` : null;
                      return highest ? (
                        <p className="text-xs text-emerald-600 font-medium mt-0.5 truncate">
                          <FaGraduationCap className="inline mr-1 text-[10px] opacity-70" />{highest}
                        </p>
                      ) : null;
                    })()}
                    <div className="mt-4 flex flex-col gap-2">
                      {al.job && (
                        <span className="inline-flex items-center justify-center gap-1.5 bg-primary-50 text-primary-700 text-xs font-semibold px-3 py-1.5 rounded-lg">
                          <FaBriefcase className="text-[10px] opacity-60" /> {al.job}
                        </span>
                      )}
                      {al.institution && (
                        <span className="inline-flex items-center justify-center gap-1.5 bg-amber-50 text-amber-700 text-xs font-semibold px-3 py-1.5 rounded-lg">
                          {al.institution}
                        </span>
                      )}
                    </div>
                    {(al.linkedin || al.google_scholar) && (
                      <div className="flex items-center justify-center gap-3 mt-5 pt-4 border-t border-gray-100">
                        {al.linkedin && <FaLinkedin className="text-gray-400 group-hover:text-blue-500 transition-colors text-lg" />}
                        {al.google_scholar && <FaGoogle className="text-gray-400 group-hover:text-red-500 transition-colors text-lg" />}
                      </div>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ─── BERITA / NEWS ─── */}
      <section className="bg-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section header */}
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between mb-12">
            <div className="home-fade-up">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center">
                  <FaNewspaper className="text-white text-sm" />
                </div>
                <span className="text-sm font-bold text-primary-600 uppercase tracking-wider">{t('home.latest')}</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 tracking-tight">{t('home.newsActivities')}</h2>
              <p className="text-gray-500 mt-2 text-lg">{t('home.newsDesc')}</p>
            </div>
            <Link to="/berita" className="mt-6 sm:mt-0 text-primary-600 hover:text-primary-700 font-semibold inline-flex items-center gap-2 px-5 py-2.5 rounded-xl hover:bg-primary-50 transition-all">
              {t('home.allNews')} <FaArrowRight className="text-sm" />
            </Link>
          </div>

          {news.length === 0 ? (
            <div className="text-center py-16 text-gray-400">
              <div className="w-20 h-20 rounded-full bg-gray-50 flex items-center justify-center mx-auto mb-5">
                <FaNewspaper className="text-3xl text-gray-300" />
              </div>
              <p className="text-lg">{t('home.noNews')}</p>
            </div>
          ) : (
            /* Bento-style grid: first card large, rest normal */
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
              {news.map((n, idx) => (
                <Link
                  key={n.id}
                  to={`/berita/${n.slug}`}
                  className={`home-news-card group relative bg-gray-50 hover:bg-white rounded-2xl border border-gray-100 hover:border-primary-200 hover:shadow-xl transition-all duration-500 overflow-hidden ${idx === 0 ? 'md:row-span-2' : ''}`}
                  style={{ animationDelay: `${idx * 100}ms` }}
                >
                  {n.cover_url ? (
                    <div className={`overflow-hidden ${idx === 0 ? 'h-52 md:h-72' : 'h-44 md:h-48'}`}>
                      <img src={n.cover_url} alt={n.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    </div>
                  ) : (
                    <div className={`bg-gradient-to-r from-primary-500 via-primary-400 to-gold-500 ${idx === 0 ? 'h-4' : 'h-3'}`}></div>
                  )}
                  <div className={`p-5 md:p-6 ${idx === 0 ? 'md:p-8' : ''}`}>
                    <div className="flex items-center gap-3 mb-3">
                      <span className={`text-xs font-bold px-3 py-1 rounded-full ${categoryColor(n.category)}`}>
                        {n.category}
                      </span>
                      <span className="text-xs text-gray-400 font-medium">{formatDate(n.created_at)}</span>
                    </div>
                    <h3 className={`font-bold text-gray-900 group-hover:text-primary-600 transition-colors mb-3 line-clamp-2 ${idx === 0 ? 'text-xl md:text-2xl' : 'text-lg'}`}>
                      {n.title}
                    </h3>
                    <p className={`text-gray-500 mb-5 ${idx === 0 ? 'text-base line-clamp-3 md:line-clamp-4' : 'text-sm line-clamp-2'}`}>{n.excerpt}</p>
                    <span className="inline-flex items-center gap-2 text-primary-600 text-sm font-semibold group-hover:gap-3 transition-all duration-300">
                      {t('home.readMore')} <FaArrowRight className="text-xs" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ─── CTA ─── */}
      <section className="relative overflow-hidden bg-gradient-to-br from-primary-900 via-primary-800 to-indigo-900 py-20 md:py-28">
        {/* Background decorations */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 right-0 w-96 h-96 bg-gold-500 opacity-[0.06] rounded-full blur-3xl translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 left-0 w-80 h-80 bg-primary-400 opacity-[0.06] rounded-full blur-3xl -translate-x-1/2 translate-y-1/2"></div>
          <div className="absolute inset-0 opacity-[0.03]" style={{backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '32px 32px'}}></div>
        </div>

        <div className="relative max-w-4xl mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm text-gold-400 text-sm font-semibold px-5 py-2 rounded-full mb-8 border border-white/10">
            <FaUsers className="text-sm" />
            {t('home.joinNow')}
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-white mb-6 leading-tight tracking-tight">
            {t('home.ctaTitle1')} <br className="hidden sm:block" />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-gold-400 to-amber-300">{t('home.ctaTitle2')}</span>
          </h2>
          <p className="text-gray-300/90 text-lg md:text-xl mb-10 max-w-2xl mx-auto leading-relaxed">
            {t('home.ctaDesc')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to={isAdmin ? '/add-alumni' : '/ajukan-alumni'}
              className="group inline-flex items-center justify-center gap-3 bg-gradient-to-r from-gold-500 to-amber-500 hover:from-gold-600 hover:to-amber-600 text-white font-bold py-4 px-10 rounded-2xl shadow-lg shadow-gold-500/25 hover:shadow-xl hover:shadow-gold-500/35 transition-all duration-300 transform hover:-translate-y-1 text-lg"
            >
              {t('home.registerNow')}
              <FaArrowRight className="group-hover:translate-x-1 transition-transform duration-300" />
            </Link>
            <Link
              to="/statistik"
              className="inline-flex items-center justify-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 hover:bg-white/15 text-white font-semibold py-4 px-8 rounded-2xl transition-all duration-300 hover:-translate-y-1"
            >
              {t('home.viewStatistics')}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
